import React, {useState} from 'react'
import PropTypes from 'prop-types'
import Slider from 'react-slick'
import {useIntl, FormattedMessage} from 'react-intl'

import {Box, Text} from '@chakra-ui/react'
import {useCustomerId, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import useSegment from '../../integrations/segment/app/hooks/use-segment'
import {useWishList} from '../../hooks/use-wish-list'

import {bundleCarouselStyles} from '../../theme/components/project/bundle-carousel'
import ProductTile from '../product-tile'
import {ArrowIcon} from '../icons'
import {
    API_ERROR_MESSAGE,
    TOAST_MESSAGE_REMOVED_FROM_WISHLIST,
    TOAST_MESSAGE_ADDED_TO_GUEST_WISHLIST
} from '../../constants'

const BundleChildCarousel = ({bundleChildProducts}) => {
    const NextArrow = (props) => {
        const {className, onClick} = props
        return (
            <Box
                className={className}
                onClick={onClick}
                role="button"
                onKeyDown={(e) => e.key === 'Enter' && onClick()}
                tabIndex={0}
            >
                <ArrowIcon />
            </Box>
        )
    }

    const PrevArrow = (props) => {
        const {className, onClick} = props
        return (
            <Box
                className={className}
                onClick={onClick}
                role="button"
                onKeyDown={(e) => e.key === 'Enter' && onClick()}
                tabIndex={0}
            >
                <ArrowIcon />
            </Box>
        )
    }

    const sliderSettings = {
        infinite: false,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        arrows: true,
        responsive: [
            {
                breakpoint: 1023,
                settings: {
                    slidesToShow: 3
                }
            },
            {
                breakpoint: 767,
                settings: {
                    slidesToShow: 2
                }
            },
            {
                breakpoint: 544,
                settings: {
                    slidesToShow: 1
                }
            }
        ]
    }

    const customerId = useCustomerId()
    const {data: wishlist} = useWishList()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const [wishlistLoading, setWishlistLoading] = useState([])
    const segment = useSegment()

    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )
    if (!bundleChildProducts || bundleChildProducts.length === 0) return null

    const addItemToWishlist = async (product, index, productDetail) => {
        setWishlistLoading([...wishlistLoading, product?.id])

        await createCustomerProductListItem(
            {
                parameters: {customerId, listId: wishlist?.id},
                body: {
                    quantity: 1,
                    public: false,
                    priority: 1,
                    type: 'product',
                    productId: product?.id
                }
            },
            {
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                        isClosable: false,
                        duration: 3000
                    })
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_ADDED_TO_GUEST_WISHLIST),
                        status: 'success',
                        isClosable: false,
                        duration: 3000
                    })
                    // segment
                    segment.sendProductToWishList(product, wishlist, index, productDetail)
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading?.filter((id) => id !== product?.id))
                }
            }
        )
    }

    const removeItemFromWishlist = async (product, index, productDetail) => {
        setWishlistLoading([...wishlistLoading, product?.id])

        const listId = wishlist.id
        const itemId = wishlist.customerProductListItems.find((i) => i.productId === product?.id).id

        await deleteCustomerProductListItem(
            {
                body: {},
                parameters: {customerId, listId, itemId}
            },
            {
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error'
                    })
                },
                onSuccess: () => {
                    toast({
                        title: formatMessage(TOAST_MESSAGE_REMOVED_FROM_WISHLIST),
                        status: 'success'
                    })
                    // segment
                    segment.sendRemoveProductFromWishlist(product, wishlist, index, productDetail)
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading.filter((id) => id !== product?.id))
                }
            }
        )
    }

    return (
        <Box sx={bundleCarouselStyles} my={'42px'}>
            <Box overflowX="hidden" className="bundle-child-carousel">
                <Text fontSize="40px" fontWeight="extrabold" mb={4} lineHeight={'52px'}>
                    <FormattedMessage defaultMessage="Bundle contains" id="bundle.contains" />
                </Text>

                <Slider {...sliderSettings}>
                    {bundleChildProducts.map((childProduct, index) => (
                        <Box key={childProduct?.product?.id | index}>
                            <ProductTile
                                product={childProduct?.product}
                                aria-label={childProduct?.product?.name}
                                productPosition={index}
                                data-testid="bundle-product-carousel"
                                dynamicImageProps={{
                                    widths: ['70vw', '70vw', '40vw', '30vw'],
                                    disWidth: 400
                                }}
                                enableFavourite
                                isFavourite={wishlist?.productWishList?.customerProductListItems?.some(
                                    (item) => item.productId === childProduct?.product?.id
                                )}
                                onFavouriteToggle={(isFavourite, productDetail) => {
                                    const action = isFavourite
                                        ? (p) => addItemToWishlist(p, index, productDetail)
                                        : (p) => removeItemFromWishlist(p, index, productDetail)
                                    return action(childProduct?.product)
                                }}
                            />
                        </Box>
                    ))}
                </Slider>
            </Box>
        </Box>
    )
}

BundleChildCarousel.propTypes = {
    bundleChildProducts: PropTypes.arrayOf(PropTypes.object),
    className: PropTypes.string,
    onClick: PropTypes.func
}

export default BundleChildCarousel
